import type { TFunction } from 'i18next';
import { TeamRole, TeamRoleDef, User } from '../types';

const ROLE_BY_FAMILY: Record<string, TeamRole> = {
  qa: 'qa',
  devops: 'devops',
  product: 'analyst',
  engineering: 'dev',
};

const ROLE_BY_PROFESSION_NAME: [RegExp, TeamRole][] = [
  [/тест|qa/i, 'qa'],
  [/devops|девопс|инфра/i, 'devops'],
  [/аналитик|analyst/i, 'analyst'],
];

/**
 * Роль участника, предложенная по его профессии. Та же логика, что в серверной
 * миграции: сначала классификация профессии (`family`), затем — её название,
 * иначе 'dev'. Роли нет в справочнике компании — первая из него. Админ всегда
 * может переопределить руками.
 */
export function suggestTeamRole(
  user: Pick<User, 'profession'> | undefined,
  roles: TeamRoleDef[],
): TeamRole {
  const profession = user?.profession;
  const name = profession?.name;
  const guess =
    (profession?.family && ROLE_BY_FAMILY[profession.family]) ||
    (name && ROLE_BY_PROFESSION_NAME.find(([pattern]) => pattern.test(name))?.[1]) ||
    'dev';
  return roles.some((r) => r.key === guess) ? guess : (roles[0]?.key ?? guess);
}

/** Роли с названием по умолчанию: пустое имя показывает (и возвращает) его. */
export const DEFAULT_ROLE_KEYS = ['dev', 'qa', 'devops', 'analyst'];

/**
 * Название роли. Переименованная — имя из справочника компании. Роль по умолчанию хранится с
 * пустым именем и показывается на языке интерфейса (teams.roleDev / roleQa / roleDevops /
 * roleAnalyst). Роли нет в справочнике — её ключ, чтобы человек не пропал.
 */
export function teamRoleName(roles: TeamRoleDef[], key: TeamRole, t: TFunction): string {
  const name = roles.find((r) => r.key === key)?.name;
  if (name) return name;
  if (DEFAULT_ROLE_KEYS.includes(key))
    return t(`teams.role${key.charAt(0).toUpperCase()}${key.slice(1)}`);
  return key;
}
